<script lang="ts">
  import { page } from '$app/stores';
  import type { ActiveEventAlerts, EventAlertInput } from '$types/races';
  import { updateEventAlerts } from '$utils/alerts';
  import { isSubscribed } from '$utils/auth';
  import toast from 'svelte-french-toast';
  import { createMenu } from 'svelte-headlessui';
  import { Transition } from 'svelte-transition';
  import TablerBell from '~icons/tabler/bell';
  import TablerBellRingingFilled from '~icons/tabler/bell-ringing-filled';

  const menu = createMenu({ label: 'Event Alerts' });

  $: ({ eventAlerts, race } = $page.data);

  $: raceAlerts = undefined as ActiveEventAlerts | undefined;
  $: {
    raceAlerts = $eventAlerts?.data?.allEventAlerts?.find((alert) => {
      return (
        alert.eventId === $race?.id &&
        alert?.alerts.find((a) => a.value !== '0')
      );
    });
  }

  $: active = raceAlerts?.alerts?.length;

  $: raceClosedAlert = raceAlerts?.alerts.find((alert) => {
    // check for null
    if (alert == null) {
      return false;
    }
    return alert.trigger === 'TIME_TO_JUMP' && alert.value == '-1';
  })
    ? true
    : false;
  $: thirtySecondAlert = raceAlerts?.alerts.find((alert) => {
    // check for null
    if (alert == null) {
      return false;
    }
    return alert.trigger === 'TIME_TO_JUMP' && alert.value == '30';
  })
    ? true
    : false;
  $: oneMinuteAlert = raceAlerts?.alerts.find((alert) => {
    // check for null
    if (alert == null) {
      return false;
    }
    return alert.trigger === 'TIME_TO_JUMP' && alert.value == '60';
  })
    ? true
    : false;
  $: fiveMinuteAlert = raceAlerts?.alerts.find((alert) => {
    // check for null
    if (alert == null) {
      return false;
    }
    return alert.trigger === 'TIME_TO_JUMP' && alert.value == '300';
  })
    ? true
    : false;
  $: thirtyMinuteAlert = raceAlerts?.alerts.find((alert) => {
    // check for null
    if (alert == null) {
      return false;
    }
    return alert.trigger === 'TIME_TO_JUMP' && alert.value == '1800';
  })
    ? true
    : false;
  $: twoHourAlert = raceAlerts?.alerts.find((alert) => {
    // check for null
    if (alert == null) {
      return false;
    }
    return alert.trigger === 'TIME_TO_JUMP' && alert.value == '7200';
  })
    ? true
    : false;
  $: newMarketAlerts = raceAlerts?.alerts.find((alert) => {
    // check for null
    if (alert == null) {
      return false;
    }
    return alert.trigger === 'PRICES_ADDED' && alert.value == '1';
  })
    ? true
    : false;
  $: newFutureMarketAlerts = raceAlerts?.alerts.find((alert) => {
    // check for null
    if (alert == null) {
      return false;
    }
    return alert.trigger === 'EVENT_ADDED' && alert.value == '1';
  })
    ? true
    : false;

  $: items = [
    {
      label: 'Race Closed',
      active: raceClosedAlert,
      alert: {
        trigger: 'TIME_TO_JUMP',
        value: '-1'
      }
    },
    {
      label: '30s before jump',
      active: thirtySecondAlert,
      alert: {
        trigger: 'TIME_TO_JUMP',
        value: '30'
      }
    },
    {
      label: '1m before jump',
      active: oneMinuteAlert,
      alert: {
        trigger: 'TIME_TO_JUMP',
        value: '60'
      }
    },
    {
      label: '5m before jump',
      active: fiveMinuteAlert,
      alert: {
        trigger: 'TIME_TO_JUMP',
        value: '300'
      }
    },
    {
      label: '30m before jump',
      active: thirtyMinuteAlert,
      alert: {
        trigger: 'TIME_TO_JUMP',
        value: '1800'
      }
    },
    {
      label: '2h before jump',
      active: twoHourAlert,
      alert: {
        trigger: 'TIME_TO_JUMP',
        value: '7200'
      }
    },
    {
      label: `New ${$race?.meeting?.track} markets`,
      sublabel: 'any new future markets at this track',
      active: newFutureMarketAlerts,
      alert: {
        trigger: 'EVENT_ADDED',
        value: newFutureMarketAlerts ? '0' : '1'
      }
    }
  ] as {
    label: string;
    sublabel?: string;
    active: boolean;
    alert: EventAlertInput;
  }[];

  async function handleChange(alert: EventAlertInput) {
    if (!$race) return;
    if (!$isSubscribed) {
      toast('You must be subscribed to create alerts!', {
        icon: '🔒',
        duration: 2000,
        position: 'bottom-center'
      });
      return;
    }

    let alerts = raceAlerts?.alerts ?? [];
    // if alert is already set, remove it
    if (
      alerts.find((a) => a.trigger === alert.trigger && a.value === alert.value)
    ) {
      alerts = alerts.filter(
        (a) => a.trigger !== alert.trigger || a.value !== alert.value
      );
    } else {
      // handle the event added differently
      if (alert.trigger === 'EVENT_ADDED') {
        // if it's already set, invert it
        if (alerts.find((a) => a.trigger === alert.trigger)) {
          alerts = alerts.map((a) => {
            if (a.trigger === alert.trigger) {
              return {
                ...a,
                value: a.value === '1' ? '0' : '1'
              };
            }
            return a;
          });
        } else {
          // otherwise add it
          alerts.push(alert);
        }
      } else {
        // otherwise add it
        alerts.push(alert);
      }
    }

    const response = await updateEventAlerts($race.id, alerts);
    if (response?.data?.updateAlerts) {
      eventAlerts?.update((resp) => {
        const raceAlerts = resp?.data?.allEventAlerts.find((alert) => {
          return alert.eventId === $race?.id;
        });
        if (raceAlerts && response.data?.updateAlerts.alerts)
          raceAlerts.alerts = response.data.updateAlerts.alerts;
        // if it's not found we need to create it
        else if ($race && response.data?.updateAlerts.alerts)
          resp?.data?.allEventAlerts.push({
            eventId: $race.id,
            alerts: response.data?.updateAlerts.alerts
          });

        //   also update all other meeting alerts
        const eventAdded = response.data?.updateAlerts.alerts.find(
          (a) => a.trigger === 'EVENT_ADDED'
        );

        if (eventAdded) {
          //   also update the meeting alert
          const meetingAlert = resp?.data?.allEventAlerts.find((alert) => {
            return (
              $race &&
              alert?.meeting?.track === $race.meeting.track &&
              alert?.meeting.location === $race.meeting.location &&
              alert?.meeting.type === $race.meeting.type
            );
          });
          if (meetingAlert) {
            const meetingAlerts = meetingAlert?.alerts.find(
              (a) => a.trigger === 'EVENT_ADDED'
            );
            if (meetingAlerts) meetingAlerts.value = eventAdded.value;
            else
              meetingAlert?.alerts.push({
                trigger: 'EVENT_ADDED',
                value: eventAdded.value
              });
          } else if ($race) {
            resp?.data?.allEventAlerts.push({
              eventId: '',
              meeting: {
                track: $race.meeting.track,
                location: $race.meeting.location,
                type: $race.meeting.type
              },
              alerts: [
                {
                  trigger: 'EVENT_ADDED',
                  value: eventAdded.value
                }
              ]
            });
          }
        }

        return resp;
      });
    }
  }
</script>

<div class="relative">
  <div use:menu.button>
    {#if !active}
      <TablerBell
        class="h-10 w-10 cursor-pointer rounded-lg p-2 text-gray-600 hover:bg-gray-300/80"
      />
    {:else}
      <TablerBellRingingFilled
        class="h-10 w-10 cursor-pointer rounded-lg p-2 text-indigo-600 hover:bg-indigo-300/40"
      />
    {/if}
  </div>
  <Transition
    show={$menu.expanded}
    enter="transition ease-out duration-100"
    enterFrom="transform opacity-0 scale-95"
    enterTo="transform opacity-100 scale-100"
    leave="transition ease-in duration-75"
    leaveFrom="transform opacity-100 scale-100"
    leaveTo="transform opacity-0 scale-95"
  >
    <div
      use:menu.items
      class="absolute z-40 mt-1 flex w-72 flex-col divide-y divide-gray-100 bg-white p-2 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
    >
      {#each items as item}
        {@const active = item.active}
        <button
          on:click|stopPropagation={() => handleChange(item.alert)}
          on:keypress|stopPropagation={() => handleChange(item.alert)}
          class="flex w-full cursor-pointer flex-row rounded-md rounded-t-md p-2 hover:bg-gray-100"
        >
          {#if item.sublabel}
            <div class="flex w-full flex-1 flex-col items-start text-sm">
              <span class="flex-1">{item.label}</span>
              <span class="w-40 text-[10px]">{item.sublabel}</span>
            </div>
          {:else}
            <span class="flex-1 text-start text-sm font-semibold"
              >{item.label}</span
            >
          {/if}
          <button
            type="button"
            class="group relative ml-2 inline-flex h-5 w-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            role="switch"
            on:click|preventDefault
            on:keydown|preventDefault
            aria-checked={active}
          >
            <span class="sr-only">{item.label}</span>
            <span
              aria-hidden="true"
              class="pointer-events-none absolute h-full w-full rounded-md bg-white"
            />
            <!-- Enabled: "bg-indigo-600", Not Enabled: "bg-gray-200" -->
            <span
              aria-hidden="true"
              class="pointer-events-none absolute mx-auto h-4 w-9 rounded-full {active
                ? 'bg-indigo-600'
                : 'bg-gray-200'} transition-colors duration-200 ease-in-out"
            />
            <!-- Enabled: "translate-x-5", Not Enabled: "translate-x-0" -->
            <span
              aria-hidden="true"
              class="{active
                ? 'translate-x-5'
                : 'translate-x-0'} pointer-events-none absolute left-0 inline-block h-5 w-5 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out"
            />
          </button>
        </button>
      {/each}
    </div>
  </Transition>
</div>
